// Localization of BUILT-IN visual-style display names.
//
// Style titles ("names") are frontend-owned display strings for a small, fixed
// catalog (~15 built-in styles). They reach the UI through three raw sinks —
// `useStyleLabel` (settings 스타일 row, canvas config tab, landing heroes),
// `StylePicker`, and `StyleLibraryModal` public cards — none of which ran the
// name through react-i18next, so they rendered in English regardless of the UI
// locale. This helper is the single localization point for all three sinks.
//
// The backend `/styles` API stays locale-free: the built-in set is fixed and
// its titles are display strings the frontend owns, mirroring the `labelKey`
// convention already used for content-language options (`contentLanguages.ts`)
// and the frontend-owned default-project-name / Director-error localization.
//
// USER CONTENT IS NEVER TRANSLATED — and the discriminator is SOURCE, not id.
// This helper matches on id (`builtInStyleLabelKey`), so it must ONLY be called
// on CATALOG-sourced names (the public `/styles` catalog and the static
// `VISUAL_STYLES` fallback). Never call it on a user-created style: a user can
// create a style whose id collides with a built-in slug (e.g. "Bold Manhwa" →
// "bold-manhwa"; the backend dup check is scoped to the user, not `_public`),
// and id-matching alone would force-translate their own name. Callers therefore
// resolve user styles in a separate branch that returns the raw name verbatim
// (see `useStyleLabel`'s user branch and `StyleLibraryModal`'s user cards).

import type { TFunction } from 'i18next'
import { builtInStyleLabelKey, styleGroupLabelKey } from '../constants/styles'
import { createNadDebug } from '../utils/debugLog'

// Feature tag: dw:style-i18n
const styleI18nLog = createNadDebug('dw:style-i18n')

/**
 * Localize a style's display name.
 *
 * @param id - The style id/slug (e.g. `"bold-manhwa"`).
 * @param rawName - The raw name from the API / static catalog / user style.
 * @param t - A react-i18next `TFunction`. The key is resolved under the
 *   `shared` namespace explicitly, so the caller's default namespace does not
 *   matter.
 * @returns The localized name for built-in styles; the raw name unchanged for
 *   user-created / unknown styles.
 */
export function localizeStyleName(
  id: string | undefined,
  rawName: string,
  t: TFunction,
): string {
  const key = id ? builtInStyleLabelKey(id) : undefined
  if (!key) {
    styleI18nLog({
      event: 'style-name-localize',
      feature: 'style-i18n',
      styleId: id ?? '',
      matched: 'passthrough',
    })
    return rawName
  }
  // `defaultValue` keeps the English label if a locale is somehow missing the
  // key (belt-and-suspenders alongside the en/ko parity gate + vi fallback).
  const localized = t(key, { ns: 'shared', defaultValue: rawName })
  styleI18nLog({
    event: 'style-name-localize',
    feature: 'style-i18n',
    styleId: id ?? '',
    matched: 'builtin',
    key,
    localized,
  })
  return localized
}

/**
 * Localize a style-group *display* label — the Style Library filter pills and
 * the "All"-view per-group section titles.
 *
 * The group *value* (`Live Action Cinema`, `3D Animation`, …, plus the `All`
 * filter and `Other` fallback) is a stable English identifier used for filter
 * identity, `tags[]` matching, and `data-testid` slugs — it is NOT display
 * text. This helper is the single localization point that turns a group value
 * into locale-appropriate label text, mirroring `localizeStyleName` for style
 * names.
 *
 * @param group - The group value (`STYLE_GROUPS` entry, `'All'`, or `'Other'`).
 * @param t - A react-i18next `TFunction`. The key resolves under the `shared`
 *   namespace explicitly, independent of the caller's default namespace.
 * @returns The localized label for recognized groups; the raw value unchanged
 *   for any unrecognized value (structural passthrough guard).
 */
export function localizeStyleGroup(group: string, t: TFunction): string {
  const key = styleGroupLabelKey(group)
  if (!key) {
    styleI18nLog({
      event: 'style-group-localize',
      feature: 'style-i18n',
      group,
      matched: 'passthrough',
    })
    return group
  }
  // `defaultValue` keeps the English label if a locale is somehow missing the
  // key (belt-and-suspenders alongside the en/ko parity gate + vi fallback).
  const localized = t(key, { ns: 'shared', defaultValue: group })
  styleI18nLog({
    event: 'style-group-localize',
    feature: 'style-i18n',
    group,
    matched: 'group',
    key,
    localized,
  })
  return localized
}
